import { useCallback, useEffect, useMemo, useState } from "react";
import { get } from "../utils/apiUtils";
import { HightlightData } from "../types/types";

type Filters = { username?: string; date?: string };

type ApiHighlight = {
  id: string;
  content: string;
  start_position: number;
  end_position: number;
  created_at: string;
  codes?: { code_id: string; code_name: string }[];
  document_name?: string;
  username?: string;
};

type ApiHighlightsResponse = {
  total: number;
  items: ApiHighlight[];
};

export const useHighlights = (
  projectId?: string,
  filters: Filters = {},
  skip = 0,
  limit = 10,
  refreshKey?: unknown
) => {
  const [highlights, setHighlights] = useState<HightlightData[]>([]);
  const [total, setTotal] = useState<number>(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const params = useMemo(
    () =>
      projectId
        ? {
            project_id: projectId,
            username: filters.username || undefined,
            date: filters.date || undefined,
            skip,
            limit,
          }
        : null,
    [projectId, filters.username, filters.date, skip, limit]
  );

  const fetchHighlights = useCallback(
    async (signal?: AbortSignal) => {
      if (!params) return;
      setLoading(true);
      setError(null);
      try {
        const res = await get<ApiHighlightsResponse | any>("/highlights", {
          params,
          signal,
        });

        const items = res?.data?.items ?? res?.data ?? [];
        const totalCount = Number(res?.data?.total ?? items.length ?? 0);

        const transformed: HightlightData[] = (items as any[]).map(
          (it: any, idx: number) => {
            const codeNames = Array.isArray(it.codes)
              ? it.codes.map((c: any) => c.code_name)
              : [];
            return {
              id: it.id ?? String(idx),
              content: it.content ?? "",
              code_name: codeNames,
              document_name: it.document_name ?? "Unknown",
              created_at: it.created_at ?? "",
              end_position: it.end_position ?? 0,
              start_position: it.start_position ?? 0,
              username: it.username ?? "",
              length: `${it.content?.length || 0} chars`,
              codesCount: codeNames.filter((n: any) => n && n !== "string")
                .length,
            };
          }
        );

        if (signal?.aborted) return; // shmang setState pas abort-it
        setHighlights(transformed);
        setTotal(totalCount);
      } catch (e: any) {
        if (signal?.aborted) return;
        console.error("Error fetching highlights:", e);
        setHighlights([]);
        setTotal(0);
        setError(e?.message || "Failed to load highlights");
      } finally {
        if (!signal?.aborted) setLoading(false);
      }
    },
    [params]
  );

  useEffect(() => {
    if (!params) return;
    const ctrl = new AbortController();
    fetchHighlights(ctrl.signal);
    return () => ctrl.abort(); // 👈 anulon kërkesën në flight (StrictMode safe)
  }, [fetchHighlights, refreshKey]);

  return {
    highlights,
    total,
    loading,
    error,
    refetch: () => fetchHighlights(),
  };
};
